import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from "prop-types";

import { useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import Draggable from 'react-draggable';
import { Tooltip } from 'components/Common/Tooltip/';

import Icon from 'components/Icons/Icon';

import './ActionBar.scss';

// A zero translate - the bar sits wherever its CSS (fixed-bottom, centred) puts it
const DEFAULT_POSITION = { x: 0, y: 0 };
// Keyboard nudge distances, in px
const NUDGE = 10;
const NUDGE_LARGE = 40;

// Clamp in the same order react-draggable does, so keyboard and mouse agree
function clamp({ x, y }, bounds) {
  return {
    x: Math.max(Math.min(x, bounds.right), bounds.left),
    y: Math.max(Math.min(y, bounds.bottom), bounds.top),
  };
}

// Translate limits that keep the bar fully on screen. `base` is the bar's box
// with no drag translate applied, i.e. where the CSS alone would place it.
function boundsFromBase(base) {
  return {
    left: -base.left,
    top: -base.top,
    right: window.innerWidth - base.left - base.width,
    bottom: window.innerHeight - base.top - base.height,
  };
}

// ActionBar component - wrapper that can contain children components. For displaying and interacting with selected sites.
// Draggable by its grip handle; starts in its default position on each mount.
function ActionBar({ serverSideSelectAll, selection, totalCount, deselect, children }) {
  const darkMode = useSelector((state) => state.app.darkMode);

  const selectedCount = serverSideSelectAll
      ? totalCount ? totalCount - selection.length : 0
      : selection.length;

  const nodeRef = useRef(null);
  const [position, setPosition] = useState(DEFAULT_POSITION);
  // Mirrors `position` so listeners/measurements never read a stale value
  const positionRef = useRef(DEFAULT_POSITION);
  // `false` means unbounded - react-draggable's default until we've measured
  const [bounds, setBounds] = useState(false);

  // The bar's undragged box, or null while it's hidden (nothing to measure)
  const measureBase = useCallback(() => {
    if (!nodeRef.current) return null;

    const { left, top, width, height } = nodeRef.current.getBoundingClientRect();
    if (!width || !height) return null;

    return {
      left: left - positionRef.current.x,
      top: top - positionRef.current.y,
      width,
      height,
    };
  }, []);

  const moveTo = useCallback(
    (next) => {
      const base = measureBase();
      const nextPosition = base ? clamp(next, boundsFromBase(base)) : next;

      positionRef.current = nextPosition;
      setPosition(nextPosition);
    },
    [measureBase]
  );

  const handleStart = () => {
    const base = measureBase();
    if (base) setBounds(boundsFromBase(base));
  };

  // react-draggable has already applied `bounds` to the values it hands us here
  const handleDrag = (event, { x, y }) => {
    positionRef.current = { x, y };
    setPosition(positionRef.current);
  };

  const handleKeyDown = (event) => {
    const step = event.shiftKey ? NUDGE_LARGE : NUDGE;
    const { x, y } = positionRef.current;

    switch (event.key) {
      case 'ArrowLeft':
        moveTo({ x: x - step, y });
        break;
      case 'ArrowRight':
        moveTo({ x: x + step, y });
        break;
      case 'ArrowUp':
        moveTo({ x, y: y - step });
        break;
      case 'ArrowDown':
        moveTo({ x, y: y + step });
        break;
      case 'Escape':
        moveTo(DEFAULT_POSITION);
        break;
      default:
        return;
    }

    event.preventDefault();
  };

  // Pull the bar back into view if the viewport changed while it was dragged
  // away - or while it was hidden and therefore unmeasurable.
  const visible = selectedCount > 0;
  useEffect(() => {
    if (visible) moveTo(positionRef.current);
  }, [visible, moveTo]);

  useEffect(() => {
    const onResize = () => moveTo(positionRef.current);

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [moveTo]);

  return (
    <Draggable
      nodeRef={nodeRef}
      handle=".action-bar-handle"
      position={position}
      bounds={bounds}
      onStart={handleStart}
      onDrag={handleDrag}
    >
      <div
        ref={nodeRef}
        className={`fixed-bottom actions-row bg-${darkMode ? 'primary' : 'light'} ${
          visible ? 'd-flex' : 'd-none'
        }`}
      >
        {/* Native title rather than <Tooltip> - an overlay would detach from the
            bar mid-drag, as popper only repositions on scroll/resize */}
        <button
          type="button"
          className="action-bar-handle"
          title="Drag to move (double-click to reset)"
          aria-label="Move the action bar. Use the arrow keys to nudge it, or Escape to reset its position."
          onDoubleClick={() => moveTo(DEFAULT_POSITION)}
          onKeyDown={handleKeyDown}
        >
          <Icon className="fas fa-grip-vertical" />
        </button>

        <div id="total-sites">
          <span id="total-sites-span">{selectedCount}</span>
          <span>{selectedCount === 1 ? 'site' : 'sites'}</span>
        </div>

        <div className="action-buttons">
          {/* Action buttons & modals go here */}
          {children}
        </div>

        <div className="deselect-container">
          <Tooltip placement="top" description="Deselect all">
            <Button
              className="deselect-all"
              variant={`${darkMode ? 'secondary' : 'light'}`}
              onClick={deselect}
            >
              <Icon className="fas fa-times" />
            </Button>
          </Tooltip>
        </div>
      </div>
    </Draggable>
  );
}

ActionBar.propTypes = {
  /** @param selection list of selected sites */
  selection: PropTypes.array.isRequired,
  /** @param deselct list of selected sites */
  deselect: PropTypes.func.isRequired,
  /** @param children - components to be rendered inside the ActionBar */
}

export default ActionBar;
